import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  MaterialIcon,
  SVGIcon,
} from '@pearsontechnology/aero-components';

import { DocumentItemShape, DocumentShape } from 'builder/Knowledge/types';
import { associationTypes } from 'builder/Knowledge/associations/types';
import { filterAssociationsTo } from 'builder/Knowledge/associations/itemAssociationFunctions';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import {
  InputKeys,
  KnowledgeTypes,
  KnowledgeMapIconIds,
  knowledgeMapDetailInputStates,
  MAXIMUM_TITLE_LENGTH,
  MAXIMUM_DESCRIPTION_LENGTH,
  PRESENTATION_ROLE,
} from 'constants/knowledgeMap';

import { getIcon } from 'Workspace/helpers';

import ButtonHumanCodingScheme from './ButtonHumanCodingScheme';

import {
  DetailsDrawerContainer,
  DetailsDrawerContent,
  DetailsDrawerHeader,
  DetailsDrawerTitleTextarea,
  DetailsDrawerTitleWrapper,
  DetailsDrawerDescriptionTextarea,
  DetailsDrawerDescriptionTextareaWrapper,
  TitleErrorInfo,
  IdCode,
} from './styles';

import messages from './messages';

class KnowledgeMapDetailsDrawer extends React.Component {
  state = {
    fullStatement: this.props.knowledgeMapItem.fullStatement,
    abbreviatedStatement: this.props.knowledgeMapItem.abbreviatedStatement,
    humanCodingScheme: this.props.knowledgeMapItem.humanCodingScheme,
    titleKey: this.props.knowledgeMapItem.titleKey,
    enableTitleTextarea: false,
    enableDescriptionTextarea: false,
    duplicateTitle: false,
    emptyTitleField: false,
  };

  doesDuplicateTitleExist = (knowlegdeMap, title) => { 
    const abbreviatedStatementLowercase = title?.toLowerCase().trim();
    return knowlegdeMap.items.some((item) =>
      item.abbreviatedStatement?.toLowerCase().trim() === abbreviatedStatementLowercase);
  };

  updateValue = () => {
    const { knowledgeMapItem, onChangeItem, knowledgeMap } = this.props;
    if (!onChangeItem) return;

    const { abbreviatedStatement, fullStatement, humanCodingScheme, titleKey } = this.state;

    const updatedAbbreviatedStatement = `${titleKey}-${abbreviatedStatement}`;

    // Only send a change event if internal state differs from props.
    if (
      abbreviatedStatement !== knowledgeMapItem.abbreviatedStatement
      || fullStatement !== knowledgeMapItem.fullStatement
      || humanCodingScheme !== knowledgeMapItem.humanCodingScheme
    ) {

      if (!abbreviatedStatement) {
        this.setState({ emptyTitleField: true });
        return;
      }
      
      if (abbreviatedStatement !== knowledgeMapItem.abbreviatedStatement &&
        this.doesDuplicateTitleExist(knowledgeMap, abbreviatedStatement)) {
        this.setState({ duplicateTitle: true, emptyTitleField: false });
        return;
      }
      
      onChangeItem({
        updatedAbbreviatedStatement,
        fullStatement,
        humanCodingScheme,
      });
    } 

    this.setState({ 
      enableTitleTextarea: false,
      duplicateTitle: false,
      emptyTitleField: false,
      enableDescriptionTextarea: false,
    });
  }

  handleOnChange = (key) => (e) => {
    this.setState({
      [key]: e.target.value,
    });
  };

  handleOnChangeHumanCodingScheme = (value) => {
    this.setState({
      humanCodingScheme: value,
    }, () => {
      this.updateValue();
    });
  };

  descriptionClickHandler = () => {
    this.setState({ enableDescriptionTextarea: true });
  }

  handleOnClose = () => {
    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }
  };

  handleOnBlur = () => {
    const { knowledgeMapItem } = this.props;
    this.setState({ 
      ...knowledgeMapItem, 
      enableTitleTextarea: false, 
      enableDescriptionTextarea: false,
      duplicateTitle: false, 
      emptyTitleField: false,
    });
  }

  handleSubmitMouseDown = (event) => event.preventDefault();

  titleClickHandler = () => {
    this.setState({ enableTitleTextarea: true });
  }

  handleKeyDown = (event) => {
    const { ESCAPE_KEY, ENTER_KEY } = InputKeys;
    const { key } = event;
    
    if (key === ESCAPE_KEY) {
      this.handleOnBlur();
    } else if (key === ENTER_KEY) {
      this.updateValue();
    }
  };

  render() {
    const { knowledgeMapItem, knowledgeMap } = this.props;
    const { SUBMIT_INPUT, PANEL_CLOSE } = KnowledgeMapIconIds;
    const { 
      abbreviatedStatement, 
      fullStatement, 
      humanCodingScheme, 
      enableTitleTextarea, 
      duplicateTitle, 
      emptyTitleField,
      enableDescriptionTextarea,
    } = this.state;
    const isReplacement = filterAssociationsTo(associationTypes.replacedBy)(knowledgeMapItem).length > 0;

    return (
      <DetailsDrawerContainer>
        <DetailsDrawerHeader>
          <ButtonHumanCodingScheme
            value={humanCodingScheme}
            onUpdate={this.handleOnChangeHumanCodingScheme}
            knowledgeMap={knowledgeMap}
            knowledgeMapItemId={knowledgeMapItem.id}
          />
          <DetailsDrawerHeader.RightActionsGroup>
            { isReplacement && (
              <em>{intl.formatMessage(messages.itemHasReplacement)}</em>
            )}
            <Button iconOnly grey onClick={this.handleOnClose}>
              <MaterialIcon id={PANEL_CLOSE} />
            </Button>
          </DetailsDrawerHeader.RightActionsGroup>
        </DetailsDrawerHeader>
        <DetailsDrawerContent>
          <DetailsDrawerTitleWrapper>
            <DetailsDrawerTitleTextarea
              labelHidden
              label={intl.formatMessage(messages.titleLabel)}
              value={abbreviatedStatement}
              placeholder={intl.formatMessage(messages.titlePlaceholder)}
              maxLength={MAXIMUM_TITLE_LENGTH}
              onChange={this.handleOnChange(knowledgeMapDetailInputStates.ABBREVIATEDSTATEMENT)}
              onKeyDown={this.handleKeyDown}
              onClick={this.titleClickHandler}
              onBlur={this.handleOnBlur}
              readOnly={!enableTitleTextarea}
            />
            {enableTitleTextarea && <span role={PRESENTATION_ROLE}>
              {/* To prevent title input blur event from getting called, We are preventing event in mousedown event  */}
              <MaterialIcon 
                onMouseDown={this.handleSubmitMouseDown} 
                onClick={this.updateValue} 
                id={SUBMIT_INPUT} 
              />
            </span>}
            {(duplicateTitle || emptyTitleField) &&
            <TitleErrorInfo>
              <SVGIcon icon={getIcon(KnowledgeTypes.WARNING_CIRCLE)} viewBox="0 0 24 24"></SVGIcon>
              {emptyTitleField ? intl.formatMessage(messages.emptyTitle) :
                intl.formatMessage(messages.duplicateTitle)}
            </TitleErrorInfo>}
          </DetailsDrawerTitleWrapper>
          {/** TODO: Should be changed with Textarea component in the future */}
          <DetailsDrawerDescriptionTextareaWrapper>
            <DetailsDrawerDescriptionTextarea
              onClick={this.descriptionClickHandler}
              enableDescriptionTextarea={enableDescriptionTextarea}
              value={fullStatement}
              onKeyDown={this.handleKeyDown}
              readOnly={!enableDescriptionTextarea}
              onBlur={this.handleOnBlur}
              placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
              onChange={this.handleOnChange(knowledgeMapDetailInputStates.FULLSTATEMENT)}
              maxLength={MAXIMUM_DESCRIPTION_LENGTH}
            />
            {enableDescriptionTextarea && <span role={PRESENTATION_ROLE}>
              <MaterialIcon onMouseDown={this.handleSubmitMouseDown} onClick={this.updateValue} id={SUBMIT_INPUT}/>
            </span>}
          </DetailsDrawerDescriptionTextareaWrapper>
          <IdCode>{knowledgeMapItem.id}</IdCode>
        </DetailsDrawerContent>
      </DetailsDrawerContainer>
    );
  }
}

KnowledgeMapDetailsDrawer.propTypes = {
  knowledgeMapItem: DocumentItemShape.isRequired,
  knowledgeMap: DocumentShape.isRequired,
  onClose: PropTypes.func,
  onChangeItem: PropTypes.func,
};

export default KnowledgeMapDetailsDrawer;
